import {
  LayoutDashboard,
  ShoppingCart,
  ChefHat,
  Users,
  UserCog,
  Heart,
  Package,
  TrendingUp,
  Sparkles,
  UtensilsCrossed,
  Percent,
  Tag,
  PieChart,
  Brain,
  Boxes,
  BookOpen,
  ClipboardList,
  Clock,
  Wallet,
  Calendar,
  Gift,
  BarChart3,
  IndianRupee,
  CreditCard,
  Scale,
  Star,
  ConciergeBell,
  DollarSign,
  FileText,
  Plug,
  Smartphone,
  QrCode,
  Megaphone,
  Settings,
  ShoppingBag,
  ShieldCheck,
  MessageSquare,
} from 'lucide-react';

/**
 * Centralized Module Configuration
 *
 * PERMISSION FIELD on menu items / children:
 *
 *   item.permission  — the SCREEN permission code from permissionCodes.js
 *                      (e.g. 'inventory.items' maps to P.INVENTORY.ITEMS.SCREEN)
 *
 * Sidebar filtering rules (implemented in ModuleSidebar):
 *   • item HAS permission field  → show only if hasPermission(item.permission) === true
 *   • item has NO permission field → always visible (unguarded entry points)
 *   • parent with children → shown only when ≥1 child is visible;
 *                            hidden when ALL children are hidden
 *
 * No module-level access shortcut — every item is checked individually.
 */

export const moduleConfig = {
  // ============================================
  // BACK OFFICE (Admin Dashboard)
  // ============================================
  BACKOFFICE: {
    id: 'BACKOFFICE',
    name: 'Back Office',
    shortName: 'Admin',
    description: 'Core restaurant management',
    basePath: '/admin',
    icon: LayoutDashboard,
    color: 'indigo',
    gradient: 'from-indigo-500 to-indigo-600',
    bgHover: 'hover:border-indigo-500 hover:bg-indigo-50/50',
    roles: ['admin', 'both'],
    features: ['Dashboard', 'Bills', 'Menu Setup'],
    menuItems: [], // Back Office uses AdminMenuConfig — permissions handled there
    valueProposition: {
      stat: '3 hrs/day',
      description: 'saved on daily operations',
      benefits: ['Real-time dashboard', 'Bill management', 'Menu configuration', 'Table management'],
    },
  },

  // ============================================
  // INVENTORY
  // ============================================
  INVENTORY: {
    id: 'INVENTORY',
    name: 'Inventory',
    shortName: 'Stock',
    description: 'Stock & recipe management',
    basePath: '/inventory',
    icon: Package,
    color: 'emerald',
    gradient: 'from-emerald-500 to-emerald-600',
    bgHover: 'hover:border-emerald-500 hover:bg-emerald-50/50',
    roles: ['admin', 'inventory', 'both'],
    features: ['Stock Items', 'Recipes', 'UoM'],
    menuItems: [
      {
        id: 'stock-overview',
        label: 'Stock Overview',
        path: '/inventory',
        icon: BarChart3,
        description: 'Stock dashboard',
        permission: 'inventory.stock_overview',   // P.INVENTORY.STOCK_OVERVIEW.SCREEN
      },
      {
        id: 'items',
        label: 'Inventory Items',
        path: '/inventory/items',
        icon: Package,
        description: 'Manage stock items',
        permission: 'inventory.items',            // P.INVENTORY.ITEMS.SCREEN
      },
      {
        id: 'recipes',
        label: 'Recipe Management',
        path: '/inventory/recipes',
        icon: BookOpen,
        description: 'Recipe management',
        permission: 'inventory.recipes',          // P.INVENTORY.RECIPES.SCREEN
      },
      {
        id: 'uom',
        label: 'Units of Measurement',
        path: '/inventory/uom',
        icon: Scale,
        description: 'Manage units',
        permission: 'inventory.uom',              // P.INVENTORY.UOM.SCREEN
      },
      {
        id: 'purchase-orders',
        label: 'Purchase Orders',
        path: '/inventory/purchase-orders',
        icon: ClipboardList,
        description: 'Manage purchase orders',
        permission: 'inventory.purchase_orders',  // P.INVENTORY.PURCHASE_ORDERS.SCREEN
      },
      {
        id: 'suppliers',
        label: 'Suppliers',
        path: '/inventory/suppliers',
        icon: Users,
        description: 'Manage suppliers',
        permission: 'inventory.supplier',         // P.INVENTORY.SUPPLIER.SCREEN
      },
      {
        id: 'stock-adjustments',
        label: 'Stock Adjustments',
        path: '/inventory/adjustments',
        icon: TrendingUp,
        description: 'Stock adjustments',
        permission: 'inventory.stock_adjustment', // P.INVENTORY.STOCK_ADJUSTMENT.SCREEN
      },
      {
        id: 'wastage',
        label: 'Wastage Tracking',
        path: '/inventory/wastage',
        icon: Percent,
        description: 'Track wastage',
        permission: 'inventory.wastage',          // P.INVENTORY.WASTAGE.SCREEN
      },
    ],
    valueProposition: {
      stat: '15% reduction',
      description: 'in food cost wastage',
      benefits: ['Real-time stock tracking', 'Recipe costing', 'Low stock alerts', 'Supplier management'],
    },
  },

  // ============================================
  // FINANCE & REPORTS
  // ============================================
REPORTS: {
  id: 'REPORTS',
  name: 'Reports',
  shortName: 'Reports',
  description: 'Analytics & reporting',
  basePath: '/finance',
  icon: PieChart,
  color: 'amber',
  gradient: 'from-amber-500 to-amber-600',
  bgHover: 'hover:border-amber-500 hover:bg-amber-50/50',
  roles: ['admin', 'accountant', 'both'],
  features: ['Sales', 'Tax', 'Analytics'],
  menuItems: [
    // ── SALES ──────────────────────────────────────────────────────────────
    {
      id: 'sales-reports',
      label: 'Sales',
      path: '/finance/sales',
      icon: TrendingUp,
      description: 'Sales analytics',
      // ❌ NO permission on parent — visibility derived from children
      children: [
        {
          id: 'daily-sales',
          label: 'Daily Sales Summary',
          path: '/finance/reports/daily-sales',
          icon: Calendar,
          permission: 'reports.sales',            // ✅ moved to each child
        },
        {
          id: 'item-wise',
          label: 'Item-wise Sales',
          path: '/finance/reports/item-wise-sales',
          icon: UtensilsCrossed,
          permission: 'reports.sales',            // ✅
        },
        {
          id: 'category-sales',
          label: 'Category-wise Sales',
          path: '/finance/reports/category-sales',
          icon: PieChart,
          permission: 'reports.sales',            // ✅
        },
        {
          id: 'hourly-sales',
          label: 'Hourly Sales Heatmap',
          path: '/finance/reports/hourly-sales',
          icon: Clock,
          permission: 'reports.sales',            // ✅ was already correct
        },
      ],
    },
    // ── FINANCIAL ──────────────────────────────────────────────────────────
    {
      id: 'financial-reports',
      label: 'Financial',
      path: '/finance/financial',
      icon: DollarSign,
      description: 'Financial analytics',
      // ❌ NO permission on parent
      children: [
        {
          id: 'payment-analysis',
          label: 'Payment Analysis',
          path: '/finance/reports/payment-analysis',
          icon: CreditCard,
          permission: 'reports.financial',        // ✅ already correct
        },
        {
          id: 'gst-report',
          label: 'GST Report',
          path: '/finance/reports/gst-report',
          icon: IndianRupee,
          permission: 'reports.financial',        // ✅
        },
        {
          id: 'discount-analysis',
          label: 'Discount Analysis',
          path: '/finance/reports/discount-analysis',
          icon: Percent,
          permission: 'reports.financial',        // ✅
        },
        {
          id: 'revenue-summary',
          label: 'Revenue Summary',
          path: '/finance/reports/revenue-summary',
          icon: BarChart3,
          permission: 'reports.financial',        // ✅
        },
      ],
    },
    // ── CUSTOMERS ──────────────────────────────────────────────────────────
    {
      id: 'customer-reports',
      label: 'Customers',
      path: '/finance/customers',
      icon: Users,
      description: 'Customer analytics',
      // ❌ NO permission on parent
      children: [
        {
          id: 'customer-insights',
          label: 'Customer Insights',
          path: '/finance/reports/customer-insights',
          icon: Star,
          permission: 'reports.customers',        // ✅ already correct
        },
        {
          id: 'loyalty-report',
          label: 'Loyalty Points',
          path: '/finance/reports/loyalty-report',
          icon: Star,
          permission: 'reports.customers',        // ✅
        },
        {
          id: 'new-vs-repeat',
          label: 'New vs Repeat',
          path: '/finance/reports/new-vs-repeat',
          icon: Users,
          permission: 'reports.customers',        // ✅
        },
      ],
    },
    // ── OPERATIONS ─────────────────────────────────────────────────────────
    {
      id: 'operational-reports',
      label: 'Operations',
      path: '/finance/operational',
      icon: ConciergeBell,
      description: 'Operations analytics',
      // ❌ NO permission on parent
      children: [
        {
          id: 'waiter-performance',
          label: 'Waiter Performance',
          path: '/finance/reports/waiter-performance',
          icon: ConciergeBell,
          permission: 'reports.operations',       // ✅ already correct
        },
        {
          id: 'table-utilization',
          label: 'Table Utilization',
          path: '/finance/reports/table-utilization',
          icon: Clock,
          permission: 'reports.operations',       // ✅
        },
        {
          id: 'kot-timing',
          label: 'KOT Timing',
          path: '/finance/reports/kot-timing',
          icon: Clock,
          permission: 'reports.operations',       // ✅
        },
      ],
    },
    // ── INVENTORY ──────────────────────────────────────────────────────────
    {
      id: 'inventory-reports',
      label: 'Inventory',
      path: '/finance/inventory',
      icon: Package,
      description: 'Inventory analytics',
      // ❌ NO permission on parent
      children: [
        {
          id: 'stock-status',
          label: 'Stock Status',
          path: '/finance/reports/stock-status',
          icon: Package,
          permission: 'reports.inventory',        // ✅ already correct
        },
//        {
//          id: 'consumption-report',
//          label: 'Consumption Report',
//          path: '/finance/reports/consumption-report',
//          icon: FileText,
//          permission: 'reports.inventory',        // ✅
//        },
      ],
    },
  ],
},

  // ============================================
  // CRM
  // ============================================
  CRM: {
    id: 'CRM',
    name: 'CRM',
    shortName: 'CRM',
    description: 'Customer & promotions',
    basePath: '/crm',
    icon: Heart,
    color: 'rose',
    gradient: 'from-rose-500 to-rose-600',
    bgHover: 'hover:border-rose-500 hover:bg-rose-50/50',
    roles: ['admin', 'marketing', 'both'],
    features: ['Customers', 'Discounts', 'Loyalty'],
    menuItems: [
      {
        id: 'customers',
        label: 'Customer Management',
        path: '/crm/customers',
        icon: Users,
        description: 'Manage customers',
        permission: 'crm.customers',              // P.CRM.CUSTOMERS.SCREEN
      },
      {
        id: 'campaigns',
        label: 'Campaigns',
        path: '/crm/campaigns',
        icon: Megaphone,
        description: 'Marketing & promotions',
        permission: 'crm.campaigns',              // P.CRM.CAMPAIGNS.SCREEN
      },
      {
        id: 'discounts',
        label: 'Discount Management',
        path: '/crm/discounts',
        icon: Tag,
        description: 'Manage discounts',
        permission: 'crm.discounts',              // P.CRM.DISCOUNTS.SCREEN
      },
      {
        id: 'loyalty',
        label: 'Loyalty Program',
        path: '/crm/loyalty',
        icon: Gift,
        description: 'Rewards program',
        permission: 'crm.loyalty',                // P.CRM.LOYALTY.SCREEN
      },
      {
    id: 'feedback',
    label: 'Customer Feedback',
    path: '/crm/feedback',
    icon: MessageSquare,                      // import MessageSquare from 'lucide-react'
    description: 'Customer reviews & ratings',
    permission: 'crm.customers',              // reuse customers screen permission, or add crm.feedback
  },
    ],
    valueProposition: {
      stat: '40% increase',
      description: 'in repeat customers',
      benefits: ['Customer database', 'Discount campaigns', 'Loyalty rewards', 'Customer insights'],
    },
  },

  // ============================================
  // HRM
  // ============================================
  HRM: {
    id: 'HRM',
    name: 'HR Management',
    shortName: 'HRM',
    description: 'Employee management',
    basePath: '/hrm',
    icon: Users,
    color: 'blue',
    gradient: 'from-blue-500 to-blue-600',
    bgHover: 'hover:border-blue-500 hover:bg-blue-50/50',
    roles: ['admin', 'hr', 'both'],
    features: ['Employees', 'Attendance', 'Payroll'],
    menuItems: [
      {
        id: 'employees',
        label: 'Employee Management',
        path: '/hrm/employees',
        icon: Users,
        description: 'Manage employees',
        permission: 'hrm.employees',              // P.HRM.EMPLOYEES.SCREEN
      },
      {
        id: 'role-permissions',
        label: 'Role Management',
        path: '/hrm/role-permissions',
        icon: ShieldCheck,
        description: 'Manage roles and permissions',
        permission: 'hrm.roles',                  // P.HRM.ROLES.SCREEN
      },
        // {
        //   id: 'attendance',
        //   label: 'Attendance',
        //   path: '/hrm/attendance',
        //   icon: Clock,
        //   description: 'Attendance tracking',
        //   comingSoon: true,
        //   permission: 'hrm.attendance',             // P.HRM.ATTENDANCE.SCREEN
        // },
        // {
        //   id: 'payroll',
        //   label: 'Payroll',
        //   path: '/hrm/payroll',
        //   icon: Wallet,
        //   description: 'Salary management',
        //   comingSoon: true,
        //   // No permission - always visible
        // },
        // {
        //   id: 'shifts',
        //   label: 'Shift Management',
        //   path: '/hrm/shifts',
        //   icon: Calendar,
        //   description: 'Schedule shifts',
        //   comingSoon: true,
        //   // No permission - always visible
        // },
    ],
    valueProposition: {
      stat: '20% lower',
      description: 'staff turnover rate',
      benefits: ['Employee database', 'Attendance tracking', 'Payroll management', 'Shift scheduling'],
    },
  },

  // ============================================
  // AI INSIGHTS (Premium)
  // ============================================
  AI_INSIGHTS: {
    id: 'AI_INSIGHTS',
    name: 'AI Insights',
    shortName: 'AI',
    description: 'Smart analytics & predictions',
    basePath: '/ai-insights',
    icon: Sparkles,
    color: 'violet',
    gradient: 'from-violet-500 to-purple-600',
    bgHover: 'hover:border-violet-500 hover:bg-violet-50/50',
    roles: ['admin', 'both'],
    features: ['Forecasting', 'Recommendations', 'Trends'],
    isPremium: true,
    menuItems: [
      {
        id: 'dashboard',
        label: 'AI Dashboard',
        path: '/ai-insights',
        icon: Brain,
        description: 'AI-powered insights',
        // No permission — always visible
      },
      {
        id: 'forecasting',
        label: 'Sales Forecasting',
        path: '/ai-insights/forecasting',
        icon: TrendingUp,
        description: 'Predict future sales',
        comingSoon: true,
      },
      {
        id: 'recommendations',
        label: 'Menu Recommendations',
        path: '/ai-insights/recommendations',
        icon: UtensilsCrossed,
        description: 'AI menu suggestions',
        comingSoon: true,
      },
    ],
    valueProposition: {
      stat: '25% boost',
      description: 'in profit margins',
      benefits: ['Sales forecasting', 'Menu optimization', 'Peak hour predictions', 'Demand analysis'],
    },
  },

  // ============================================
  // POS — Standalone
  // ============================================
  POS: {
    id: 'POS',
    name: 'Point of Sale',
    shortName: 'POS',
    description: 'Order management & billing',
    basePath: '/pos',
    icon: ShoppingCart,
    color: 'teal',
    gradient: 'from-teal-500 to-teal-600',
    bgHover: 'hover:border-teal-500 hover:bg-teal-50/50',
    roles: ['pos', 'admin', 'both'],
    features: ['Quick Billing', 'Table Orders', 'Split Bills'],
    isStandalone: true,
    menuItems: [],
    valueProposition: {
      stat: '50% faster',
      description: 'order processing time',
      benefits: ['Quick billing', 'Table management', 'Split bills', 'Multi-payment support'],
    },
  },

  // ============================================
  // KDS — Standalone
  // ============================================
  KDS: {
    id: 'KDS',
    name: 'Kitchen Display',
    shortName: 'KDS',
    description: 'Real-time kitchen order tracking',
    basePath: '/kds',
    icon: ChefHat,
    color: 'orange',
    gradient: 'from-orange-500 to-orange-600',
    bgHover: 'hover:border-orange-500 hover:bg-orange-50/50',
    roles: ['pos', 'kds', 'admin', 'both'],
    features: ['Live Orders', 'Status Updates', 'Priority Queue'],
    isStandalone: true,
    menuItems: [],
    valueProposition: {
      stat: '30% faster',
      description: 'kitchen order completion',
      benefits: ['Real-time order display', 'Status tracking', 'Priority queue', 'Multi-station support'],
    },
  },

  // ============================================
  // INTEGRATIONS
  // ============================================
  INTEGRATIONS: {
    id: 'INTEGRATIONS',
    name: 'Integrations',
    shortName: 'Connect',
    description: 'Third-party integrations',
    basePath: '/integrations',
    icon: Plug,
    color: 'cyan',
    gradient: 'from-cyan-500 to-cyan-600',
    bgHover: 'hover:border-cyan-500 hover:bg-cyan-50/50',
    roles: ['admin', 'both'],
    features: ['Payments', 'Delivery', 'Accounting'],
    menuItems: [
      {
        id: 'all-integrations',
        label: 'All Integrations',
        path: '/integrations',
        icon: Plug,
        description: 'Manage all integrations',
        // No permission — always visible
      },
    ],
    valueProposition: {
      stat: '10+ platforms',
      description: 'ready to connect',
      benefits: ['Payment gateways', 'Delivery platforms', 'Accounting sync', 'SMS & WhatsApp'],
    },
  },

  // ============================================
  // CAPTAIN APP
  // ============================================
  CAPTAIN_APP: {
    id: 'CAPTAIN_APP',
    name: 'Captain App',
    shortName: 'App',
    description: 'Mobile app for waiters',
    basePath: '/captain-app',
    icon: Smartphone,
    color: 'sky',
    gradient: 'from-sky-500 to-sky-600',
    bgHover: 'hover:border-sky-500 hover:bg-sky-50/50',
    roles: ['admin', 'both'],
    features: ['Order Taking', 'Table Mgmt', 'KOT'],
    menuItems: [
      {
        id: 'captain-app-download',
        label: 'Download App',
        path: '/captain-app',
        icon: Smartphone,
        description: 'Get the Captain App',
        // No permission — always visible
      },
    ],
    valueProposition: {
      stat: '50% faster',
      description: 'order taking at tables',
      benefits: ['Mobile order taking', 'Real-time sync', 'Offline mode', 'Kitchen notifications'],
    },
  },

  // ============================================
  // QR MENU
  // ============================================
  QR_MENU: {
    id: 'QR_MENU',
    name: 'QR Menu',
    shortName: 'QR',
    description: 'Digital menu & self-ordering',
    basePath: '/qr-menu',
    icon: QrCode,
    color: 'purple',
    gradient: 'from-purple-500 to-purple-600',
    bgHover: 'hover:border-purple-500 hover:bg-purple-50/50',
    roles: ['admin', 'both'],
    features: ['Digital Menu', 'Self-Order', 'Contactless'],
    menuItems: [
      {
        id: 'qr-settings',
        label: 'Settings',
        path: '/qr-menu/settings',
        icon: Settings,
        description: 'QR menu configuration',
        permission: 'qr_menu.settings',           // P.QR_MENU.SETTINGS.SCREEN
      },
      {
        id: 'qr-codes',
        label: 'QR Codes',
        path: '/qr-menu/qr-codes',
        icon: QrCode,
        description: 'Manage QR codes',
        permission: 'qr_menu.qr_codes',           // P.QR_MENU.QR_CODES.SCREEN
      },
      {
        id: 'qr-orders',
        label: 'Orders',
        path: '/qr-menu/orders',
        icon: ShoppingBag,
        description: 'Manage QR orders',
        permission: 'qr_menu.orders',             // P.QR_MENU.ORDERS.SCREEN
      },
    ],
    valueProposition: {
      stat: '30% more',
      description: 'table turnover rate',
      benefits: [
        'QR code menu for each table',
        'Customers order from their phone',
        'Digital menu with images & prices',
        'Allergen & dietary filters',
        'Multi-language support',
        'Upsell suggestions & combos',
        'Real-time menu updates',
        'Reduce waiter dependency',
      ],
    },
  },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

export const getModuleById = (moduleId) => moduleConfig[moduleId] || null;

export const getModuleByPath = (pathname) =>
  Object.values(moduleConfig).find((module) => pathname.startsWith(module.basePath));

export const getAllModules = () => Object.values(moduleConfig);

export const getModulesForRole = (userRole, hasRoleFn) =>
  Object.values(moduleConfig).filter((module) => {
    if (!userRole) return true;
    return module.roles.some((role) => hasRoleFn(role));
  });

export const getOperationsModules = () =>
  ['POS', 'KDS', 'BACKOFFICE', 'INVENTORY'].map((id) => moduleConfig[id]).filter(Boolean);

export const getManagementModules = () =>
  ['HRM', 'CRM', 'REPORTS', 'AI_INSIGHTS', 'INTEGRATIONS', 'CAPTAIN_APP', 'QR_MENU']
    .map((id) => moduleConfig[id])
    .filter(Boolean);

export const getModulesWithAccess = (enabledModules = []) =>
  getAllModules().map((module) => ({
    ...module,
    isEnabled: enabledModules.includes(module.id),
    isLocked: !enabledModules.includes(module.id),
  }));

export const getOperationsModulesWithAccess = (enabledModules = []) =>
  getOperationsModules().map((module) => ({
    ...module,
    isEnabled: enabledModules.includes(module.id),
    isLocked: !enabledModules.includes(module.id),
  }));

export const getManagementModulesWithAccess = (enabledModules = []) =>
  getManagementModules().map((module) => ({
    ...module,
    isEnabled: enabledModules.includes(module.id),
    isLocked: !enabledModules.includes(module.id),
  }));

export const subscriptionPlans = {
  basic: {
    name: 'Basic',
    modules: ['POS', 'BACKOFFICE', 'CAPTAIN_APP'],
    description: 'Essential POS and back office features',
  },
  pro: {
    name: 'Professional',
    modules: ['POS', 'KDS', 'BACKOFFICE', 'INVENTORY', 'REPORTS', 'CRM', 'INTEGRATIONS', 'CAPTAIN_APP', 'QR_MENU'],
    description: 'Full restaurant management suite',
  },
  enterprise: {
    name: 'Enterprise',
    modules: ['POS', 'KDS', 'BACKOFFICE', 'INVENTORY', 'REPORTS', 'CRM', 'HRM', 'AI_INSIGHTS', 'INTEGRATIONS', 'CAPTAIN_APP', 'QR_MENU'],
    description: 'Complete solution with AI insights and HR management',
  },
};

export const getModulesForPlan = (planId) => {
  const plan = subscriptionPlans[planId];
  if (!plan) return [];
  return plan.modules.map((id) => moduleConfig[id]).filter(Boolean);
};

export const moduleColorSchemes = {
  teal:    { gradient: 'from-teal-500 to-teal-600',       active: 'bg-teal-50 text-teal-600',       indicator: 'bg-teal-600',     border: 'border-teal-500',     bg: 'bg-teal-500',     hover: 'hover:bg-teal-600'    },
  indigo:  { gradient: 'from-indigo-500 to-indigo-600',   active: 'bg-indigo-50 text-indigo-600',   indicator: 'bg-indigo-600',   border: 'border-indigo-500',   bg: 'bg-indigo-500',   hover: 'hover:bg-indigo-600'  },
  amber:   { gradient: 'from-amber-500 to-amber-600',     active: 'bg-amber-50 text-amber-600',     indicator: 'bg-amber-600',    border: 'border-amber-500',    bg: 'bg-amber-500',    hover: 'hover:bg-amber-600'   },
  emerald: { gradient: 'from-emerald-500 to-emerald-600', active: 'bg-emerald-50 text-emerald-600', indicator: 'bg-emerald-600',  border: 'border-emerald-500',  bg: 'bg-emerald-500',  hover: 'hover:bg-emerald-600' },
  blue:    { gradient: 'from-blue-500 to-blue-600',       active: 'bg-blue-50 text-blue-600',       indicator: 'bg-blue-600',     border: 'border-blue-500',     bg: 'bg-blue-500',     hover: 'hover:bg-blue-600'    },
  rose:    { gradient: 'from-rose-500 to-rose-600',       active: 'bg-rose-50 text-rose-600',       indicator: 'bg-rose-600',     border: 'border-rose-500',     bg: 'bg-rose-500',     hover: 'hover:bg-rose-600'    },
  violet:  { gradient: 'from-violet-500 to-purple-600',   active: 'bg-violet-50 text-violet-600',   indicator: 'bg-violet-600',   border: 'border-violet-500',   bg: 'bg-violet-500',   hover: 'hover:bg-violet-600'  },
  orange:  { gradient: 'from-orange-500 to-orange-600',   active: 'bg-orange-50 text-orange-600',   indicator: 'bg-orange-600',   border: 'border-orange-500',   bg: 'bg-orange-500',   hover: 'hover:bg-orange-600'  },
  cyan:    { gradient: 'from-cyan-500 to-cyan-600',       active: 'bg-cyan-50 text-cyan-600',       indicator: 'bg-cyan-600',     border: 'border-cyan-500',     bg: 'bg-cyan-500',     hover: 'hover:bg-cyan-600'    },
  sky:     { gradient: 'from-sky-500 to-sky-600',         active: 'bg-sky-50 text-sky-600',         indicator: 'bg-sky-600',      border: 'border-sky-500',      bg: 'bg-sky-500',      hover: 'hover:bg-sky-600'     },
  purple:  { gradient: 'from-purple-500 to-purple-600',   active: 'bg-purple-50 text-purple-600',   indicator: 'bg-purple-600',   border: 'border-purple-500',   bg: 'bg-purple-500',   hover: 'hover:bg-purple-600'  },
};

export default moduleConfig;